import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { useCourses } from '../../contexts/CourseContext';
import { getProgramClasses } from '../../services/programClassService';
import { supabase } from '../../lib/supabase';

const CourseList: React.FC = () => {
  const navigate = useNavigate();
  const { courses, loading, error, deleteCourse, updateCourse, refreshCourses } = useCourses();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedCourses, setEditedCourses] = useState<Record<string, any>>({});
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [classCounts, setClassCounts] = useState<Record<string, { total: number, recruiting: number }>>({});

  const categoryOptions = [
    { id: 'all', label: '전체' },
    { id: 'group', label: '단체체험' },
    { id: 'preschool', label: '유치과정' },
    { id: 'elementary', label: '초등과정' },
    { id: 'secondary', label: '중·고등과정' },
    { id: 'adult', label: '성인과정' },
    { id: 'special', label: '특별과정' },
    { id: 'camp', label: '방학캠프' },
    { id: 'international', label: '국제교류' }
  ];

  const statusOptions = [
    { id: 'all', label: '전체' },
    { id: 'upcoming', label: '모집예정' },
    { id: 'recruiting', label: '모집중' },
    { id: 'ongoing', label: '진행중' },
    { id: 'completed', label: '종료' }
  ];

  const statusLabels = {
    upcoming: { label: '모집예정', color: 'bg-amber-50 text-amber-600 border border-amber-200' },
    recruiting: { label: '모집중', color: 'bg-emerald-50 text-emerald-600 border border-emerald-200' },
    ongoing: { label: '진행중', color: 'bg-indigo-50 text-indigo-600 border border-indigo-200' },
    completed: { label: '종료', color: 'bg-slate-50 text-slate-500 border border-slate-200' }
  };

  // 실제 강의 상태를 계산하는 함수 (날짜와 시간 모두 체크)
  const getActualCourseStatus = (course: typeof courses[0]): string => {
    // 수동 모드인 경우 설정된 status 그대로 사용
    if (course.status_mode === 'manual') {
      return course.status;
    }

    // 자동 모드: 날짜 기반 상태 계산

    // 날짜 필드가 없으면 기본값 'upcoming' 반환
    if (!course.registrationStartDate || !course.registrationEndDate ||
        !course.courseStartDate || !course.courseEndDate) {
      return 'upcoming';
    }

    // 한국 시간(KST) 기준으로 현재 시간 계산
    const now = new Date();
    const kstOffset = 9 * 60; // KST는 UTC+9
    const localOffset = now.getTimezoneOffset(); // 로컬 타임존의 UTC 오프셋 (분 단위, 음수)
    const kstTime = new Date(now.getTime() + (kstOffset + localOffset) * 60 * 1000);

    const today = kstTime.toISOString().split('T')[0];

    // 교육 종료 → completed
    if (today > course.courseEndDate) {
      return 'completed';
    }

    // 교육 중 → ongoing
    if (today >= course.courseStartDate && today <= course.courseEndDate) {
      return 'ongoing';
    }

    // 모집 기간 및 시간 체크 (KST 기준)
    // TIME 타입은 "HH:MM:SS" 형식으로 올 수 있으므로 "HH:MM" 추출
    const startTime = course.registrationStartTime
      ? course.registrationStartTime.substring(0, 5)
      : '00:00';
    const endTime = course.registrationEndTime
      ? course.registrationEndTime.substring(0, 5)
      : '23:59';

    const registrationStartStr = `${course.registrationStartDate}T${startTime}:00+09:00`;
    const registrationEndStr = `${course.registrationEndDate}T${endTime}:00+09:00`;

    const registrationStart = new Date(registrationStartStr);
    const registrationEnd = new Date(registrationEndStr);

    // 모집 중 → recruiting (모집 기간 및 시간 내)
    if (now >= registrationStart && now <= registrationEnd) {
      return 'recruiting';
    }

    // 모집 예정 (모집 시작 전) → upcoming
    if (now < registrationStart) {
      return 'upcoming';
    }

    // 모집 종료 후 교육 시작 전 → upcoming으로 표시
    return 'upcoming';
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.courseName.toLowerCase().includes(searchTerm.toLowerCase());
    const actualStatus = getActualCourseStatus(course);
    const matchesStatus = filterStatus === 'all' || actualStatus === filterStatus;
    const matchesCategory = filterCategory === 'all' ||
                           (Array.isArray(course.category) && course.category.includes(filterCategory as any));
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('정말로 이 강의를 삭제하시겠습니까?')) {
      deleteCourse(id);
      alert('강의가 삭제되었습니다.');
    }
    setOpenDropdownId(null);
  };

  const handleRowClick = (courseId: string) => {
    if (!isEditMode) {
      navigate(`/admin/courses/${courseId}`);
    }
  };

  const handlePublishToggle = async (courseId: string, currentStatus: boolean, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    try {
      await updateCourse(courseId, { is_published: !currentStatus });
    } catch (err) {
      alert('공개 상태 변경에 실패했습니다.');
    }
  };

  const handleEditChange = (courseId: string, field: string, value: any) => {
    setEditedCourses(prev => ({
      ...prev,
      [courseId]: {
        ...(prev[courseId] || {}),
        [field]: value
      }
    }));
  };

  const handleSaveEdit = async () => {
    try {
      // 일괄 저장: Supabase에 직접 업데이트 (매번 refresh 방지)
      const updates = Object.entries(editedCourses)
        .filter(([_, changes]) => Object.keys(changes).length > 0)
        .map(([courseId, changes]) => {
          // Course 타입을 Supabase programs 타입으로 변환
          const updateData: any = {};
          if (changes.courseName) updateData.name = changes.courseName;
          if (changes.status) updateData.status = changes.status;
          if (changes.is_published !== undefined) updateData.is_published = changes.is_published;
          if (changes.show_in_catalog !== undefined) updateData.show_in_catalog = changes.show_in_catalog;
          if (changes.show_in_registration !== undefined) updateData.show_in_registration = changes.show_in_registration;

          return supabase
            .from('programs')
            .update(updateData)
            .eq('id', courseId);
        });

      // 모든 업데이트를 병렬로 실행
      await Promise.all(updates);

      // 모든 업데이트 완료 후 한 번만 데이터 새로고침
      await refreshCourses();

      setEditedCourses({});
      setIsEditMode(false);
      alert('변경사항이 저장되었습니다.');
    } catch (err) {
      console.error('일괄 저장 실패:', err);
      alert('저장에 실패했습니다.');
    }
  };

  const handleCancelEdit = () => {
    setEditedCourses({});
    setIsEditMode(false);
  };

  // Load class counts for all programs
  useEffect(() => {
    const loadClassCounts = async () => {
      const counts: Record<string, { total: number, recruiting: number }> = {};
      for (const course of courses) {
        try {
          const classes = await getProgramClasses(course.id);

          // 모집 중인 반 개수 계산 (is_published=true & 모집 기간 내)
          const now = new Date();
          const recruitingClasses = classes.filter(cls => {
            if (!cls.is_published) return false;

            const startTime = cls.registration_start_time?.substring(0, 5) || '00:00';
            const endTime = cls.registration_end_time?.substring(0, 5) || '23:59';
            const startDateTime = new Date(`${cls.registration_start_date}T${startTime}:00+09:00`);
            const endDateTime = new Date(`${cls.registration_end_date}T${endTime}:00+09:00`);

            return now >= startDateTime && now <= endDateTime;
          });

          counts[course.id] = {
            total: classes.length,
            recruiting: recruitingClasses.length
          };
        } catch (err) {
          console.error(`Failed to load classes for program ${course.id}:`, err);
          counts[course.id] = { total: 0, recruiting: 0 };
        }
      }
      setClassCounts(counts);
    };

    if (courses.length > 0) {
      loadClassCounts();
    }
  }, [courses]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-indigo-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-4">오류: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-150"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">강의 목록</h1>
          <p className="text-sm text-gray-500">프로그램별 메타 정보를 관리합니다. 실제 반 단위 모집은 '반/모집 관리' 메뉴를 이용하세요.</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {isEditMode ? (
            <>
              <button
                onClick={handleCancelEdit}
                className="flex-1 sm:flex-none px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 whitespace-nowrap transition-colors duration-150"
              >
                취소
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 sm:flex-none px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 whitespace-nowrap transition-colors duration-150"
              >
                저장
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditMode(true)}
                className="flex-1 sm:flex-none flex items-center justify-center px-4 py-2 text-sm border border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 whitespace-nowrap transition-colors duration-150"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                일괄 편집
              </button>
              <button
                onClick={() => navigate('/admin/courses/new')}
                className="flex-1 sm:flex-none flex items-center justify-center px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 whitespace-nowrap transition-colors duration-150"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                새 강의 등록
              </button>
            </>
          )}
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6 p-4 space-y-4">
        {/* 검색창 */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="강의명으로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-150"
          />
        </div>

        {/* 카테고리 필터 */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">프로그램 유형</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categoryOptions.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilterCategory(category.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 whitespace-nowrap ${
                  filterCategory === category.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* 상태 필터 */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">상태</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {statusOptions.map((status) => (
              <button
                key={status.id}
                onClick={() => setFilterStatus(status.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 whitespace-nowrap ${
                  filterStatus === status.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 강의 목록 테이블 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20 whitespace-nowrap">
                  공개
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 whitespace-nowrap">
                  이미지
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{width: '200px', maxWidth: '200px'}}>
                  강의명
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 whitespace-nowrap">
                  카테고리
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 whitespace-nowrap">
                  상태
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32 whitespace-nowrap">
                  반 현황
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24 whitespace-nowrap">
                  프로그램
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24 whitespace-nowrap">
                  수강신청
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 whitespace-nowrap">
                  기관/장소
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16 whitespace-nowrap">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCourses.map((course) => {
                const editedData = editedCourses[course.id] || {};
                const actualStatus = getActualCourseStatus(course);
                return (
                  <tr
                    key={course.id}
                    onClick={() => handleRowClick(course.id)}
                    className={`${isEditMode ? '' : 'cursor-pointer hover:bg-gray-50'}`}
                  >
                    <td className="px-4 py-4 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {isEditMode ? (
                        <input
                          type="checkbox"
                          checked={course.is_published}
                          onChange={(e) => handlePublishToggle(course.id, course.is_published, e)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                        />
                      ) : (
                        <span className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${
                          course.is_published ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-slate-50 text-slate-600 border border-slate-200'
                        }`}>
                          {course.is_published ? '공개' : '비공개'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {course.courseImage ? (
                        <img
                          src={course.courseImage}
                          alt={course.courseName}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-gray-400 text-xs">No</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => isEditMode && e.stopPropagation()} style={{width: '200px', maxWidth: '200px'}}>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={editedData.courseName ?? course.courseName}
                          onChange={(e) => handleEditChange(course.id, 'courseName', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <div className="text-sm font-medium text-gray-900 truncate" title={course.courseName}>
                          {course.courseName}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {course.category && course.category.length > 0 ? (
                          course.category.slice(0, 2).map((cat, idx) => {
                            const categoryLabel = categoryOptions.find(opt => opt.id === cat)?.label || cat;
                            return (
                              <span
                                key={idx}
                                className="px-2 py-1 text-xs font-medium bg-indigo-50 text-indigo-600 rounded border border-indigo-200"
                              >
                                {categoryLabel}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => isEditMode && e.stopPropagation()}>
                      {isEditMode ? (
                        <select
                          value={editedData.status ?? course.status}
                          onChange={(e) => handleEditChange(course.id, 'status', e.target.value)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="upcoming">모집예정</option>
                          <option value="recruiting">모집중</option>
                          <option value="ongoing">진행중</option>
                          <option value="completed">종료</option>
                        </select>
                      ) : (
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          statusLabels[actualStatus as keyof typeof statusLabels]?.color || 'bg-amber-50 text-amber-600 border border-amber-200'
                        }`}>
                          {statusLabels[actualStatus as keyof typeof statusLabels]?.label || '모집예정'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium ${
                          (classCounts[course.id]?.total || 0) === 0
                            ? 'bg-slate-50 text-slate-600 border border-slate-200'
                            : 'bg-indigo-50 text-indigo-600 border border-indigo-200'
                        }`}>
                          총 {classCounts[course.id]?.total || 0}개
                        </span>
                        {(classCounts[course.id]?.recruiting || 0) > 0 && (
                          <span className="inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-200">
                            모집중 {classCounts[course.id]?.recruiting}개
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap" onClick={(e) => isEditMode && e.stopPropagation()}>
                      {isEditMode ? (
                        <input
                          type="checkbox"
                          checked={editedData.show_in_catalog ?? course.show_in_catalog ?? false}
                          onChange={(e) => handleEditChange(course.id, 'show_in_catalog', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${
                          course.show_in_catalog ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-slate-50 text-slate-400 border border-slate-200'
                        }`}>
                          {course.show_in_catalog ? '노출' : '숨김'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap" onClick={(e) => isEditMode && e.stopPropagation()}>
                      {isEditMode ? (
                        <input
                          type="checkbox"
                          checked={editedData.show_in_registration ?? course.show_in_registration ?? false}
                          onChange={(e) => handleEditChange(course.id, 'show_in_registration', e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${
                          course.show_in_registration ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-slate-50 text-slate-400 border border-slate-200'
                        }`}>
                          {course.show_in_registration ? '노출' : '숨김'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-xs text-gray-700 whitespace-nowrap">
                      {course.institution || course.location || '-'}
                    </td>
                    <td className="px-3 py-4 text-center relative whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdownId(openDropdownId === course.id ? null : course.id);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>
                      {openDropdownId === course.id && (
                        <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg border border-gray-200 z-10 overflow-hidden">
                          <div className="py-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownId(null);
                                navigate(`/admin/courses/edit/${course.id}`);
                              }}
                              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                            >
                              <PencilIcon className="h-4 w-4 mr-2" />
                              수정
                            </button>
                            <button
                              onClick={(e) => handleDelete(course.id, e)}
                              className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                            >
                              <TrashIcon className="h-4 w-4 mr-2" />
                              삭제
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredCourses.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">검색 결과가 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseList;
